import { Component } from '@theme/component';
import { buildPillOption, ConfiguratorStateEvent, renderPrice, normalizeLieferzeit } from '@theme/configurator-shared';
import { GenericConfiguratorPicker } from '@theme/configurator-generic-picker';
import { CartLinesUpdateEvent, CartErrorEvent, ProductSelectEvent } from '@shopify/events';
import {
  isMobileBreakpoint,
  mediaQueryLarge,
  lockScroll,
  unlockScroll,
  onAnimationEnd,
  isClickedOutside,
  yieldToMainThread,
  preloadImage,
} from '@theme/utilities';
import { getScrollTop, scrollTo } from '@theme/scroll-container';

/**
 * @typedef {object} ConfiguratorVariant
 * @property {number} id
 * @property {string} farbe
 * @property {string} kissenGroesse
 * @property {string} deckeGroesse
 * @property {string} lieferzeit
 * @property {number} priceCents
 * @property {number | null} compareAtPriceCents
 * @property {boolean} available
 */

/**
 * @typedef {object} ConfiguratorRefs
 * @property {HTMLScriptElement} variantData
 * @property {HTMLElement} farbeGroup
 * @property {HTMLElement} kissenList
 * @property {HTMLElement} deckeGroup
 * @property {HTMLElement} priceDisplay
 * @property {HTMLElement} lieferzeitDisplay
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} errorMessage
 * @property {HTMLElement} infoView
 * @property {HTMLTemplateElement} infoIconTemplate
 * @property {HTMLElement} infoContent
 * @property {HTMLElement} infoFooter
 * @property {HTMLDialogElement} infoDialog
 */

/**
 * @extends Component<ConfiguratorRefs>
 */
class ConfiguratorVariantPicker extends Component {
  /** @type {ConfiguratorVariant[]} */
  #variants = [];

  /** @type {string} */
  #farbe = '';

  /** @type {{ kissenGroesse: string }[]} */
  #kissen = [{ kissenGroesse: '' }];

  /** @type {{ deckeGroesse: string } | null} */
  #decke = null;

  /**
   * True for exactly the first #renderDeckeGroup call after
   * #initializeDefaultSelection resolved a bare Kissen-only URL variant
   * (`?variant=<id>` with no Decke — e.g. a link from the Kissen collection).
   * Without this, #renderDeckeGroup's "no Decke selected yet → default to the
   * first Decke option" branch immediately overrides the shopper's explicit
   * "Keine Decke" arrival, redirecting the page to a Kissen+Decke variant.
   * Consumed (and cleared) on that first render; every later render — Farbe
   * change, Kissen-1 size change — keeps the default-to-first-option behavior.
   * @type {boolean}
   */
  #deckeInitiallyNone = false;

  /**
   * True when the shopper arrived via a bare Kissen-only `?variant=` URL —
   * the same condition #deckeInitiallyNone captures, set alongside it in
   * #initializeDefaultSelection — but unlike that one-shot flag, this
   * persists across renders instead of being consumed after one, since the
   * Decke picker should stay hidden (replaced by a "+ Decke hinzufügen"
   * button, see #renderDeckeCollapsed) until the shopper explicitly asks to
   * add one, regardless of any Farbe or Kissen-1 changes in between.
   * Cleared only by #handleShowDecke(); never re-set by a render. See
   * docs/superpowers/specs/2026-09-03-collapsed-decke-for-kissen-arrival-design.md.
   * @type {boolean}
   */
  #deckeCollapsed = false;

  /**
   * True while the shopper's most recent explicit Decke choice was "Keine
   * Decke". A PERSISTENT flag (not a one-shot): it keeps #renderDeckeGroup()'s
   * "!this.#decke → default to options[0]" auto-default from re-adding a Decke
   * on every later re-render — a Farbe change, a Kissen size change — so a
   * shopper who removed the Decke stays on the bare Kissen (gallery, price,
   * Lieferzeit, add-to-cart) until they ask for one again.
   *
   * Set by the "Keine Decke" pill's onChange. Cleared by every action that
   * selects a Decke: a real Decke-size pill, #handleShowDecke() ("+ Decke
   * hinzufügen"), #handleAddBettwaescheSet() ("+ Bettwäsche Set"). NOT touched
   * by a move into an all-40/40 (no-pairing) selection: #renderDeckeGroup()'s
   * `options.length === 0` early return is upstream of the flag logic, and
   * keeping the flag set there is deliberate — the shopper still has no Decke.
   *
   * Distinct from #deckeInitiallyNone, which stays a one-shot: bare-arrival
   * persistence is handled separately by #deckeCollapsed (which renders the
   * collapsed "+ Decke hinzufügen" button and returns from #renderDeckeGroup()
   * before the flag logic is reached).
   *
   * Was a one-shot until the Kissen-gallery post-QA fix — see
   * docs/superpowers/specs/2026-09-08-configurator-kissen-gallery-image-design.md
   * "Post-QA fixes (v3) — A".
   * @type {boolean}
   */
  #deckeExplicitlyNone = false;

  /** Aborts a stale gallery-refresh fetch if a newer Farbe change supersedes it. */
  #galleryAbortController = new AbortController();

  /**
   * The variant id #updateGallery() last dispatched a section fetch for. Lets
   * the method no-op when the resolved gallery variant hasn't changed — a
   * Decke-size ↔ Decke-size switch (still a Decke selected → still the Farbe's
   * first variant), or an add-Kissen-at-end (doesn't shift #deckePairingIndex
   * or #decke). Reset to null in #bindFarbeInputs' change handler so a colour
   * switch always refetches. Also reset to null when the section fetch fails, so
   * a later interaction resolving the same id still retries instead of
   * no-op'ing into a stale gallery. Persists across a sticky-modal portal
   * reconnect (private field; the instance survives the Node.append move).
   * @type {number | null}
   */
  #lastGalleryVariantId = null;

  /**
   * Variant id → raw response text, populated by a hover/focus-triggered speculative
   * fetch of that variant's product page (see `#preloadSwatchGallery`) and consumed by
   * `#updateGallery()` on a real click. An explicit in-memory cache, NOT reliance on the
   * browser's HTTP cache: Shopify's product-page response sends `Cache-Control: no-cache,
   * no-store` (confirmed via live-tested response headers - deliberate, since the page
   * carries session/cart-sensitive rendering), so a second `fetch()` to the identical URL
   * is guaranteed to re-hit the network rather than being served from HTTP cache - the
   * browser is correctly honoring the server's explicit directive. Storing the raw text
   * (not a parsed `Document`) so each consumer gets its own fresh
   * `DOMParser().parseFromString()` call, matching what a real (uncached) fetch already
   * provides today - a shared `Document` object read/mutated by multiple call sites would
   * be a foot-gun `#updateGallery()`'s current contract was never designed for. Only ever
   * grows for the lifetime of the picker instance - the variant catalog for one product is
   * small and bounded, so this isn't a meaningful memory concern.
   * @type {Map<number, string>}
   */
  #preloadedGalleryResponses = new Map();

  /**
   * Variant ids currently in flight for `#preloadSwatchGallery`'s speculative fetch, to
   * avoid issuing a duplicate request for the same variant on repeated hovers before the
   * first one has resolved into `#preloadedGalleryResponses`. Cleared when the shopper
   * commits to a Farbe (see `#bindFarbeInputs`), alongside aborting the in-flight
   * preloads themselves.
   * @type {Set<number>}
   */
  #preloadsInFlight = /** @type {Set<number>} */ (new Set());

  /** Farbe → variant id, resolved via a hover/focus-triggered speculative Section
   * Rendering lookup (see `#preloadSwatchGallery`) for colors not in `this.#variants`
   * (i.e. every color except the currently-selected one). Kept separate from
   * `this.#variants` so a mere hover never mutates the picker's actual selection state. */
  #preloadedVariantIds = /** @type {Map<string, number>} */ (new Map());

  /** Farbe → that color's bare Kissen-only variant id (no Decke), implicitly scoped
   * to the `#kissen[0]` size that was selected when the color was resolved — the id
   * `#updateGallery()` fetches when `#decke == null`. Resolved in the same
   * `#preloadSwatchGallery` pass as `#preloadedVariantIds`, from the same data, so a
   * hover warms the Kissen photo's section render too — not just the Freisteller's.
   * Cleared when the shopper commits to a Farbe (see `#bindFarbeInputs`) so a
   * since-then Kissen-size change can't serve a stale entry. A color with no bare
   * variant in that size never gets an entry. */
  #preloadedKissenVariantIds = /** @type {Map<string, number>} */ (new Map());

  /**
   * Signal for `#preloadSwatchGallery` / `#warmGalleryResponse` speculative
   * fetches. Aborted — and immediately replaced with a fresh controller — at the
   * top of `#bindFarbeInputs`' `change` handler: once the shopper commits to a
   * colour, the in-flight preloads for every OTHER swatch are dead weight
   * contending with the real fetches that `change` is about to issue
   * (`#fetchVariantsForFarbe`, `#updateGallery`, and `media-gallery.js`'s
   * hero-image download). The replacement controller keeps later preloads
   * working (a `signal` stays aborted forever once fired, so the field must be
   * swapped, not just aborted).
   *
   * NOT aborted from `disconnectedCallback`: this component disconnects /
   * reconnects without a real teardown (the sticky-modal portal move — see
   * `#initialized`'s doc comment) while the same JS instance and its caches keep
   * living; a page navigation aborts in-flight fetches on its own anyway.
   * @type {AbortController}
   */
  #preloadAbortController = new AbortController();

  /** Aborts a stale variants-refresh fetch if a newer Farbe change supersedes it. */
  #variantsAbortController = new AbortController();

  /** @type {boolean} */
  #isBettwaesche = false;

  /**
   * Whether the product has any Decke-related Konfektion/Naht content to
   * show in the size-info dialog (see
   * blocks/configurator-variant-picker.liquid's has_decke_info). Gates
   * whether the Decke-side info trigger (Decke legend button, oversized-pill
   * "i" icon) is built at all — with no trigger, the dialog can never be
   * opened from that side.
   * @type {boolean}
   */
  #hasDeckeInfo = false;

  /**
   * Whether the product has any Kissen-related Konfektion/Naht content to
   * show in the size-info dialog (see
   * blocks/configurator-variant-picker.liquid's has_kissen_info). Gates the
   * Kissen legend's own info trigger (#buildKissenRow), independent of
   * #hasDeckeInfo — a product can have one without the other. Both triggers
   * open the exact same dialog/slide (handleOpenInfo has no per-trigger
   * variation in what opens; the dialog's own content already shows the
   * Kissen and Decke sections side by side when both exist), so there is no
   * separate #hasKissenInfo-gated method — handleOpenInfo's own guard is
   * `#hasDeckeInfo || #hasKissenInfo`.
   * @type {boolean}
   */
  #hasKissenInfo = false;

  /** @type {GenericConfiguratorPicker | null} */
  #genericPicker = null;

  /**
   * True once `connectedCallback` has run its one-time setup at least once.
   * Guards against `assets/sticky-configurator-modal.js`'s element-portaling
   * (moving this same element into/out of its mobile modal via `Node.append`,
   * which per the Custom Elements spec triggers a full
   * `disconnectedCallback` → `connectedCallback` cycle on every move, not
   * just a visual reposition) from silently resetting the shopper's
   * already-made Kissen/Decke/Farbe selection back to the page's default —
   * see docs/superpowers/specs/2026-08-27-sticky-modal-portal-reset-design.md.
   * @type {boolean}
   */
  #initialized = false;

  /**
   * The exact three deckeGroesse values that require two side seams (see
   * docs/superpowers/specs/2026-08-23-configurator-groesseninformationen-design.md)
   * — an explicit allowlist per that design, not derived from string-parsing
   * the value or from a per-variant metafield flag.
   * @type {string[]}
   */
  static #OVERSIZED_DECKE_GROESSEN = ['200/200', '200/220', '240/220'];

  /**
   * Ceiling on `#preloadSwatchGallery` speculative section-render fetches in
   * flight at once. A pointer sweeping the swatch row would otherwise queue one
   * (post-v3: two) fetch per colour, and those renders compete with the real
   * click-time gallery fetch and `media-gallery.js`'s hero-image download. The
   * real fetch in `#updateGallery()` does not go through the capped path.
   * @type {number}
   */
  static #MAX_CONCURRENT_PRELOADS = 3;

  connectedCallback() {
    this.#isBettwaesche = this.dataset.isBettwaesche === 'true';
    this.#hasDeckeInfo = this.dataset.hasDeckeInfo === 'true';
    this.#hasKissenInfo = this.dataset.hasKissenInfo === 'true';

    // requiredRefs must be set before super.connectedCallback() — the base
    // Component class's own connectedCallback runs the requiredRefs check
    // synchronously (see assets/component.js's #updateRefs), so setting it
    // after would be too late. Each path only requires the refs its own
    // Liquid branch actually renders (see
    // blocks/configurator-variant-picker.liquid) — lieferzeitDisplay,
    // farbeGroup, kissenList, and deckeGroup only exist in the Bettwäsche
    // branch; optionGroups only exists in the generic branch. The "add
    // Kissen" button is no longer a static ref — it's built and repositioned
    // in JS by #buildKissenRow, so it can live inline with the last Kissen
    // instance's own legend instead of as a fixed row below the whole list.
    // infoView/infoIconTemplate/infoContent/infoFooter/infoDialog are common
    // to BOTH branches — the Liquid file renders that markup once, outside
    // the is_bettwaesche if/else, so handleOpenInfo() (and its "Konfiguration
    // anzeigen" trigger next to the generic path's own Größe option) works
    // the same way regardless of which configurator type is active.
    const infoRefs = ['infoView', 'infoIconTemplate', 'infoContent', 'infoFooter', 'infoDialog'];
    this.requiredRefs = this.#isBettwaesche
      ? [
          'variantData',
          'farbeGroup',
          'farbeName',
          'kissenList',
          'deckeGroup',
          'priceDisplay',
          'lieferzeitDisplay',
          'addToCartButton',
          'errorMessage',
          ...infoRefs,
        ]
      : [
          'variantData',
          'optionGroups',
          'priceDisplay',
          'lieferzeitDisplay',
          'addToCartButton',
          'errorMessage',
          ...infoRefs,
        ];

    super.connectedCallback();

    if (this.#isBettwaesche) {
      mediaQueryLarge.addEventListener('change', this.#handleBreakpointChange);
    }

    if (!this.#isBettwaesche) {
      this.#genericPicker = new GenericConfiguratorPicker(this);
      this.#genericPicker.connect();
      return;
    }

    // Only the FIRST connect should parse the embedded variant catalog and
    // seed the default Kissen/Decke/Farbe selection, or bind the (static,
    // server-rendered, never-recreated) Farbe radio inputs' change
    // listeners. A later reconnect — e.g. assets/sticky-configurator-modal.js
    // moving this element into/out of its mobile modal via Node.append,
    // which triggers a full disconnectedCallback → connectedCallback cycle
    // — must NOT re-run either: #initializeDefaultSelection would silently
    // discard whatever the shopper had already selected (this.#kissen/
    // #decke/#farbe are private fields that persist across the move just
    // fine on their own, since the JS instance itself isn't destroyed), and
    // re-running #bindFarbeInputs on the SAME still-attached radio inputs
    // would stack a duplicate 'change' listener on each one every time the
    // modal opens, compounding indefinitely. #renderKissenList/
    // #renderDeckeGroup/#render still run every time — they rebuild their
    // own DOM from whatever #kissen/#decke currently hold and are safe to
    // call repeatedly, which is exactly what's needed to restore correct
    // rendering after the move. See
    // docs/superpowers/specs/2026-08-27-sticky-modal-portal-reset-design.md.
    if (!this.#initialized) {
      this.#initialized = true;
      this.#variants = JSON.parse(this.refs.variantData.textContent ?? '[]');
      this.#initializeDefaultSelection();
      this.#bindFarbeInputs();

      // Post-fix-A the gallery's steady-state target when no Decke is selected is
      // the current colour's bare Kissen-only variant. Warm its section HTML now
      // so a straight-to-"Keine Decke" click — no prior Farbe change, no swatch
      // hover — is instant. A Farbe change aborts this fetch (the shopper's on a
      // different colour then) and a hover would have warmed it anyway, so the
      // payoff is that one narrow path; the cost is one aborted-on-first-change
      // low-priority request. Skip on a bare-Kissen `?variant=` arrival:
      // #deckeInitiallyNone means #decke is already null and the server already
      // rendered the Kissen photo.
      if (!this.#deckeInitiallyNone) {
        const currentKissenId = this.#kissenOnlyVariantId(this.#variants, this.#farbe, this.#kissen[0]?.kissenGroesse);
        if (currentKissenId != null) this.#warmGalleryResponse(currentKissenId);
      }
    }

    this.#renderKissenList();
    this.#renderDeckeGroup();
    this.#render();
  }

  /**
   * Picks the default selection: the variant Shopify already resolved
   * server-side (from a `?variant=` URL param, e.g. a shared/bookmarked
   * link), if present among the embedded variants — honored exactly, even
   * if it's a bare Kissen-only variant, since a customer who followed a
   * link to a specific variant expects to see that variant, not a
   * different one. Falls back to the first combined (Kissen+Decke) variant
   * per the spec's "default on load is a combined variant, not bare" rule
   * when there's no URL-selected variant to honor.
   */
  #initializeDefaultSelection() {
    const selectedVariantId = Number.parseInt(this.dataset.selectedVariantId ?? '', 10);
    const urlVariant = this.#variants.find((v) => v.id === selectedVariantId);
    const defaultVariant = urlVariant ?? this.#variants.find((v) => v.deckeGroesse !== '') ?? this.#variants[0];
    if (!defaultVariant) return;

    this.#farbe = defaultVariant.farbe;
    this.#kissen = [{ kissenGroesse: defaultVariant.kissenGroesse }];
    this.#decke = defaultVariant.deckeGroesse ? { deckeGroesse: defaultVariant.deckeGroesse } : null;

    // The shopper followed a link to a specific bare Kissen-only variant
    // (no Decke). Tell the first #renderDeckeGroup not to auto-select a Decke,
    // and keep the whole Decke picker collapsed behind an add button until
    // they explicitly ask for one (#deckeCollapsed).
    this.#deckeInitiallyNone = urlVariant != null && !defaultVariant.deckeGroesse;
    this.#deckeCollapsed = this.#deckeInitiallyNone;
  }

  #bindFarbeInputs() {
    const inputs = this.refs.farbeGroup.querySelectorAll('input[type="radio"]');
    for (const input of inputs) {
      input.addEventListener('change', async () => {
        if (!(input instanceof HTMLInputElement)) return;

        // The shopper committed to a colour — the speculative preloads for every
        // OTHER swatch are now worthless and only contend with the real fetches
        // this change is about to issue. Cancel them and re-arm. Keep the
        // already-stored responses and id maps (still useful if the shopper
        // clicks back to a colour they hovered earlier).
        this.#preloadAbortController.abort();
        this.#preloadAbortController = new AbortController();
        // Empties the set synchronously; the aborted fetches' own
        // .finally(() => #preloadsInFlight.delete(id)) then no-op on absent keys.
        this.#preloadsInFlight.clear();
        // #preloadedKissenVariantIds is keyed by Farbe but the id it holds is
        // implicitly scoped to the Kissen size that was selected when the colour
        // was resolved. A size change since then would make a stale entry serve
        // the wrong bare id — drop the lot; it only ever saved a Layer-1 lookup.
        this.#preloadedKissenVariantIds.clear();

        this.#farbe = input.value;
        await this.#fetchVariantsForFarbe(input.dataset.optionValueId);
        // A newer Farbe change may have landed while this fetch was in flight
        // (its abort only stops the fetch itself, not this continuation) —
        // bail rather than render a stale farbe/variants pairing.
        if (this.#farbe !== input.value) return;

        // Warm the new colour's bare Kissen-only section HTML — but ONLY when a
        // Decke is currently selected, so #updateGallery() below is not about to
        // fetch this same id itself. (With #decke == null the gallery already
        // targets the bare-Kissen variant, so #updateGallery() cold-fetches it
        // directly and warming it here would just duplicate that request,
        // racing it on a different abort signal.) With a Decke selected, this
        // makes the shopper's next "Keine Decke" toggle on the new colour
        // instant. Deduped: a prior hover preload of this id makes it a no-op.
        if (this.#decke != null) {
          const newKissenId = this.#kissenOnlyVariantId(this.#variants, this.#farbe, this.#kissen[0]?.kissenGroesse);
          if (newKissenId != null) this.#warmGalleryResponse(newKissenId);
        }

        this.#renderKissenList();
        this.#renderDeckeGroup();
        this.#render();
        // Farbe change always refreshes the gallery, even if the numeric id
        // somehow matched the previous fetch.
        this.#lastGalleryVariantId = null;
        this.#updateGallery();
      });

      // Speculative preload (spec design §4, layer 2): fetch this color's product page
      // ahead of an actual click and store the response in `#preloadedGalleryResponses`
      // for `#updateGallery()` to consume directly - NOT relying on the browser's HTTP
      // cache (see that field's doc comment for why: Shopify sends `Cache-Control:
      // no-cache, no-store` on this response, so a second `fetch()` to the same URL is
      // guaranteed to hit the network again regardless of any fetch option here).
      // `priority: 'low'` keeps this from contending with any real in-flight request;
      // cheap even if the user never clicks this swatch.
      const preload = () => {
        if (!(input instanceof HTMLInputElement)) return;
        this.#preloadSwatchGallery(input.value, input.dataset.optionValueId);
      };
      input.addEventListener('pointerenter', preload);
      input.addEventListener('focus', preload);

      // `pointerenter` covers touch too on the browsers live-tested this session
      // (confirmed on real iOS Safari: a tap dispatches pointerover → pointerenter →
      // pointerdown → touchstart, all within a few ms of each other - pointerenter alone
      // already fires and already gives the preload a real head start before `change`).
      // `touchstart` is kept as a defensive fallback for engines/input paths where
      // `pointerenter` might not fire for touch (real historical inconsistency across
      // browsers) - `#preloadSwatchGallery`'s own in-flight/response dedup (see
      // `#preloadsInFlight`/`#preloadedGalleryResponses`) makes it safe for both to fire
      // for the same tap without triggering a duplicate network request; only
      // `pointerdown` was dropped here since it's redundant with `pointerenter` on every
      // browser observed live-testing this (one extra event, zero additional coverage).
      // `{ passive: true }` since this listener never calls preventDefault() and must not
      // block the browser's own scroll/touch handling.
      input.addEventListener('touchstart', preload, { passive: true });
    }
  }

  /**
   * Speculatively fetches a color's product page HTML (the same request
   * `#updateGallery()` needs on actual selection) ahead of a click, storing the raw
   * response text in `#preloadedGalleryResponses` for `#updateGallery()` to consume
   * directly instead of issuing its own fetch. Deduped per-variant via
   * `#preloadedGalleryResponses`/`#preloadsInFlight` - only one fetch in flight (and one
   * cached response kept) per variant per page load.
   *
   * `this.#variants` only ever holds the CURRENTLY-selected color's variant set (see
   * `#fetchVariantsForFarbe`'s doc comment - the full catalog can't be embedded upfront
   * per Shopify's 250-variant Liquid cap), so hovering any OTHER color can't resolve a
   * variant id from it directly - that lookup would silently miss every time, which is
   * exactly the color a hover-to-preload is meant to help with. Resolve a representative
   * variant id for the hovered color via the same Section Rendering API request
   * `#fetchVariantsForFarbe` uses, but WITHOUT touching `this.#farbe`/`this.#variants` -
   * a mere hover must never mutate the picker's actual selection state (that would corrupt
   * price/Add-to-Cart/render() if the user hovers one swatch then clicks a different one,
   * or never clicks at all). Cached separately per Farbe via `#preloadedVariantIds` so a
   * repeated hover doesn't re-issue this lookup fetch either.
   * @param {string} farbe
   * @param {string | undefined} optionValueId
   */
  async #preloadSwatchGallery(farbe, optionValueId) {
    const productUrl = this.dataset.productUrl;
    const sectionId = this.dataset.sectionId;
    if (!productUrl) return;

    let variantId = this.#variants.find((v) => v.farbe === farbe)?.id ?? this.#preloadedVariantIds.get(farbe);

    /** This color's bare Kissen-only variant id, if resolvable without an extra
     * fetch: from this.#variants (current color), the Layer-1 fetchedVariants
     * below (other colors, first hover), or #preloadedKissenVariantIds (repeat
     * hover). May stay undefined — a color with no bare Kissen-only variant, or
     * a non-current color whose Layer-1 lookup didn't run because variantId was
     * already cached; the click-time #fetchVariantsForFarbe path covers that. */
    // Scope to the picker's current first-Kissen size — the exact size
    // #updateGallery() will request for this colour too (a Farbe change never
    // resets #kissen).
    const targetKissenGroesse = this.#kissen[0]?.kissenGroesse;
    let kissenVariantId = this.#currentFarbeIs(farbe)
      ? this.#kissenOnlyVariantId(this.#variants, farbe, targetKissenGroesse)
      : this.#preloadedKissenVariantIds.get(farbe);

    if (variantId == null) {
      if (!sectionId || !optionValueId) return;

      try {
        const response = await fetch(`${productUrl}?section_id=${sectionId}&option_values=${optionValueId}`, {
          priority: 'low',
          signal: this.#preloadAbortController.signal,
        });
        const responseText = await response.text();
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const variantDataScript = html.querySelector('configurator-variant-picker script[type="application/json"]');
        if (!variantDataScript?.textContent) return;

        /** @type {ConfiguratorVariant[]} */
        const fetchedVariants = JSON.parse(variantDataScript.textContent);
        variantId = fetchedVariants.find((v) => v.farbe === farbe)?.id;
        if (variantId == null) return;

        this.#preloadedVariantIds.set(farbe, variantId);

        if (kissenVariantId == null) {
          kissenVariantId = this.#kissenOnlyVariantId(fetchedVariants, farbe, targetKissenGroesse);
        }
        if (kissenVariantId != null) this.#preloadedKissenVariantIds.set(farbe, kissenVariantId);
      } catch {
        // Best-effort only - a failed speculative lookup just means the real click-time
        // flow (#fetchVariantsForFarbe → #updateGallery) runs uncached, same as today.
        return;
      }
    }

    // Warm each distinct id through the shared dedup + cap bookkeeping.
    // kissenVariantId first: post-fix-A the `#decke == null` gallery branch
    // (Kissen photo) is the common steady state, so when the concurrency cap
    // (#warmGalleryResponse) is nearly full — mid-sweep — the Kissen photo
    // claims the last slot. variantId feeds #updateGallery()'s Decke-selected
    // branch (Freisteller).
    for (const id of new Set([kissenVariantId, variantId].filter((v) => v != null))) {
      this.#warmGalleryResponse(id);
    }
  }

  /**
   * Speculatively fetches one variant's product-page section HTML and stores the
   * raw text in `#preloadedGalleryResponses` for `#updateGallery()` to consume
   * directly. Deduped (skips an id already cached or in flight) and capped
   * (`#MAX_CONCURRENT_PRELOADS` — a swatch sweep must not pile up renders that
   * starve the real click fetch). Best-effort: a failure just means
   * `#updateGallery()` fetches that id uncached, same as with no preload.
   * `priority: 'low'` + `#preloadAbortController.signal` (aborted on a real Farbe
   * commit — see `#bindFarbeInputs`).
   * @param {number} id
   */
  #warmGalleryResponse(id) {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;
    if (this.#preloadedGalleryResponses.has(id) || this.#preloadsInFlight.has(id)) return;
    if (this.#preloadsInFlight.size >= ConfiguratorVariantPicker.#MAX_CONCURRENT_PRELOADS) return;

    this.#preloadsInFlight.add(id);

    fetch(`${productUrl}?variant=${id}`, { priority: 'low', signal: this.#preloadAbortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#preloadedGalleryResponses.set(id, responseText);
      })
      .catch(() => {
        // Best-effort only - a failed speculative preload just means the real click-time
        // fetch in #updateGallery() runs uncached, same as today.
      })
      .finally(() => {
        this.#preloadsInFlight.delete(id);
      });
  }

  /** True when `farbe` is the picker's currently-selected color (so its full
   * variant set is in `this.#variants`). */
  #currentFarbeIs(farbe) {
    return this.#variants.some((v) => v.farbe === farbe);
  }

  /**
   * The bare Kissen-only variant id for `farbe` + `kissenGroesse` in `pool` (no
   * Decke) — the EXACT id `#updateGallery()`'s `#decke == null` branch fetches,
   * i.e. `#resolvedVariants()[0]`, which resolves against `#kissen[0]`'s size.
   * Callers pass `this.#kissen[0]?.kissenGroesse` so the warmed section HTML is
   * keyed to the id the real click will request — not a different-size bare
   * variant (which would cache-miss on every colour whose catalog-first bare
   * variant isn't the selected size). Same bare predicate as `#resolvedVariants()`
   * and `assets/kissen-collection.js`. `undefined` when the colour has no bare
   * variant in that size — correct: `#updateGallery()` then finds no
   * `#resolvedVariants()[0]` either and falls back, so there's nothing to warm.
   * @param {ConfiguratorVariant[]} pool
   * @param {string} farbe
   * @param {string | undefined} kissenGroesse
   * @returns {number | undefined}
   */
  #kissenOnlyVariantId(pool, farbe, kissenGroesse) {
    if (kissenGroesse == null) return undefined;
    return pool.find(
      (v) => v.farbe === farbe && v.kissenGroesse === kissenGroesse && (v.deckeGroesse === '' || v.deckeGroesse == null)
    )?.id;
  }

  /**
   * Re-fetches the variant set scoped to a single Farbe via the Section
   * Rendering API (?section_id=...&option_values=...), the same mechanism
   * assets/variant-picker.js uses for the native picker. Required because
   * blocks/configurator-variant-picker.liquid only embeds variants for the
   * page's initially-resolved Farbe — Shopify hard-caps product.variants at
   * 250 in Liquid, so the full catalog can never be embedded upfront for
   * high-variant products. Falls back to leaving #variants untouched (stale
   * but non-empty) on fetch failure, since a broken fetch shouldn't wipe out
   * an already-working picker.
   * @param {string | undefined} optionValueId
   */
  async #fetchVariantsForFarbe(optionValueId) {
    const productUrl = this.dataset.productUrl;
    const sectionId = this.dataset.sectionId;
    if (!productUrl || !sectionId || !optionValueId) return;

    this.#variantsAbortController.abort();
    this.#variantsAbortController = new AbortController();

    const url = `${productUrl}?section_id=${sectionId}&option_values=${optionValueId}`;

    try {
      const response = await fetch(url, { signal: this.#variantsAbortController.signal });
      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      const variantDataScript = html.querySelector('configurator-variant-picker script[type="application/json"]');
      if (!variantDataScript?.textContent) return;

      this.#variants = JSON.parse(variantDataScript.textContent);
    } catch (error) {
      if (error?.name !== 'AbortError') console.error(error);
    }
  }

  /**
   * Distinct kissenGroesse values available for the current Farbe.
   * @returns {string[]}
   */
  #availableKissenGroessen() {
    const values = this.#variants.filter((v) => v.farbe === this.#farbe).map((v) => v.kissenGroesse);
    return [...new Set(values)];
  }

  /**
   * True if at least one kissenGroesse value for the current Farbe has an
   * in-stock BARE (no-Decke) variant — the exact shape handleAddKissen()
   * always adds (a new Kissen instance never starts Decke-paired). Checking
   * "any deckeGroesse pairing" here would be wrong: a size whose only
   * in-stock SKU is a combined Kissen+Decke variant has nothing purchasable
   * to add as a bare instance, even though the size itself "has stock".
   * Independent of the max-instance-count check in #renderKissenList();
   * both must pass for the "+ weiteres Kissen hinzufügen" button to show.
   * @returns {boolean}
   */
  #hasAvailableKissenGroesse() {
    return this.#variants.some((v) => v.farbe === this.#farbe && v.deckeGroesse === '' && v.available);
  }

  #renderKissenList() {
    this.refs.kissenList.innerHTML = '';
    const options = this.#availableKissenGroessen();
    const maxKissen = Number.parseInt(this.dataset.maxKissen ?? '4', 10);
    const canAddKissen = this.#kissen.length < maxKissen && this.#hasAvailableKissenGroesse();

    this.#kissen.forEach((instance, index) => {
      const isLast = index === this.#kissen.length - 1;
      const row = this.#buildKissenRow(instance, index, options, isLast && canAddKissen);
      this.refs.kissenList.append(row);
    });
  }

  handleAddKissen() {
    const maxKissen = Number.parseInt(this.dataset.maxKissen ?? '4', 10);
    if (this.#kissen.length >= maxKissen) return;

    const firstAvailable =
      this.#variants.find((v) => v.farbe === this.#farbe && v.deckeGroesse === '' && v.available)?.kissenGroesse ??
      this.#availableKissenGroessen()[0] ??
      '';
    this.#kissen.push({ kissenGroesse: firstAvailable });
    this.#renderKissenList();
    this.#render();
  }

  /**
   * @param {number} index
   */
  #handleRemoveKissen(index) {
    this.#kissen.splice(index, 1);
    // Removing any instance can shift which one #deckePairingIndex()
    // returns (e.g. removing the instance the Decke was paired with) —
    // re-derive it, same as every other Kissen-list mutation does.
    this.#renderDeckeGroup();
    this.#renderKissenList();
    this.#render();
    // Removing the Decke-pairing instance can flip #decke to null (via
    // #renderDeckeGroup above) and move #resolvedVariants()[0] — refresh.
    this.#updateGallery();
  }

  /**
   * Kissen legend/pill label for a given Kissen instance's index: Kissen 1
   * (index 0) uses the unnumbered label, Kissen 2+ uses the numbered label
   * with `{{ number }}` replaced by the 1-based position. Shared by
   * #buildKissenRow (row legend) and #buildPillSummary (sticky bar pill).
   * @param {number} index
   * @returns {string}
   */
  #kissenLabel(index) {
    return index === 0
      ? this.dataset.kissenLabel ?? 'Kissen'
      : (this.dataset.kissenNumberedLabel ?? 'Kissen {{ number }}').replace('{{ number }}', String(index + 1));
  }

  /**
   * Appends `text` followed by a visually-subdued, smaller
   * data-size-unit-suffix span (e.g. "(in cm)") to `target`. Used by the
   * Kissen and Decke fieldset labels only — NOT #buildPillSummary's sticky
   * bar pills, which stay unsuffixed since the actual size value already
   * appears right next to the plain label there.
   * @param {HTMLElement} target
   * @param {string} text
   */
  #appendSizeLabel(target, text) {
    target.append(text);

    const suffix = this.dataset.sizeUnitSuffix;
    if (!suffix) return;

    const suffixEl = document.createElement('span');
    suffixEl.className = 'configurator-variant-picker__size-unit-suffix';
    suffixEl.textContent = `${suffix}`;
    target.append(suffixEl);
  }

  /**
   * @param {{ kissenGroesse: string }} instance
   * @param {number} index
   * @param {string[]} options
   * @param {boolean} showAddButton - Whether to render the "add another
   *   Kissen" button inline with this row's own label. Only ever true for
   *   the LAST currently-rendered Kissen row (and only while under
   *   data-max-kissen), so the button visually follows whichever label is
   *   currently last as Kissen instances are added/removed, per the
   *   original design's intent (pushed right, same row as the label).
   * @returns {HTMLElement}
   */
  #buildKissenRow(instance, index, options, showAddButton) {
    // Whichever instance #deckePairingIndex() returns resolves against the
    // CURRENT Decke selection (see #resolvedVariants' own doc comment) —
    // every other instance always resolves against its bare variant. A
    // size pill's own availability must follow the same resolution rule,
    // or the checked pill for the pairing instance can show as unavailable
    // even though its actual (combined) resolved variant is in stock —
    // confirmed live on Inverness/Navy, where 80/80's only in-stock SKU is
    // the 80/80+135/200 combined variant, with no bare 80/80 SKU at all.
    const isPairingInstance = index === this.#deckePairingIndex();
    const pairingDeckeGroesse = this.#decke?.deckeGroesse ?? '';
    // Wraps a label row + the fieldset as siblings, NOT a <legend> inside
    // the fieldset — <legend> has non-standard flex-item sizing in browsers
    // (a documented quirk: even inside a `display: flex` fieldset, legend
    // retains block-like auto-wrapping behavior no CSS override reliably
    // fixes), which was forcing the add-Kissen button onto its own line
    // regardless of available width. A plain <span>, styled identically and
    // associated to the fieldset via aria-labelledby, sidesteps the quirk
    // entirely while keeping the same accessible fieldset-label semantics.
    const wrapper = document.createElement('div');

    const labelId = `configurator-kissen-label-${index}-${this.dataset.productId}`;
    const labelRow = document.createElement('div');
    labelRow.className = 'configurator-variant-picker__kissen-legend-row';

    const label = document.createElement('span');
    label.id = labelId;
    label.className = 'configurator-variant-picker__kissen-legend';
    this.#appendSizeLabel(label, this.#kissenLabel(index));
    labelRow.append(label);

    // Independent of the Decke legend's own info trigger (#renderDeckeGroup)
    // — gated on #hasKissenInfo, not #hasDeckeInfo, so a product with only
    // Kissen content (or only Decke content) shows exactly one trigger, not
    // both or neither. Both open the exact same handleOpenInfo dialog/slide.
    // index === 0 only — Konfektion/Naht content is product-level (identical
    // regardless of which Kissen instance), so repeating the trigger on
    // every additional Kissen row (2nd, 3rd, 4th) would just be redundant.
    if (this.#hasKissenInfo && index === 0) {
      const infoLink = document.createElement('button');
      infoLink.type = 'button';
      infoLink.className = 'configurator-variant-picker__kissen-info-link button-unstyled';
      infoLink.textContent = this.dataset.sizeInfoButtonLabel ?? 'Show size information';
      infoLink.addEventListener('click', () => this.handleOpenInfo());
      labelRow.append(infoLink);
    }

    if (showAddButton) {
      const addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.className = 'configurator-variant-picker__add-kissen button-unstyled';
      addButton.textContent = this.dataset.addKissenLabel ?? 'Kissen hinzufügen';
      addButton.addEventListener('click', () => this.handleAddKissen());
      labelRow.append(addButton);
    }

    wrapper.append(labelRow);

    const row = document.createElement('fieldset');
    row.className = 'variant-option variant-option--buttons';
    row.setAttribute('aria-labelledby', labelId);

    for (const value of options) {
      // Options with no matching bare variant at all are already excluded from
      // `options` upstream (see #availableKissenGroessen) — a variant that exists
      // but is sold out still renders here, just visually subdued and non-checkable
      // via aria-disabled, per spec ("shown, not hidden"). For the pairing
      // instance specifically, availability is checked against the SAME
      // deckeGroesse #resolvedVariants() would actually resolve this size
      // to (the current Decke selection) rather than always the bare
      // variant — otherwise a size whose only in-stock SKU is a combined
      // Kissen+Decke variant would show its own selected pill as sold out.
      const matchingVariant = this.#variants.find(
        (v) =>
          v.farbe === this.#farbe &&
          v.kissenGroesse === value &&
          v.deckeGroesse === (isPairingInstance ? pairingDeckeGroesse : '')
      );
      const pillLabel = buildPillOption({
        value,
        text: value,
        name: `configurator-kissen-${index}-${this.dataset.productId}`,
        checked: value === instance.kissenGroesse,
        available: matchingVariant?.available ?? true,
        soldOutLabel: this.dataset.soldOutLabel ?? 'Sold out',
        onChange: () => {
          this.#kissen[index] = { kissenGroesse: value };
          // Any Kissen instance's size change can shift which instance
          // #deckePairingIndex() returns (e.g. this one becoming, or
          // ceasing to be, the first pairable size) — always re-render,
          // not just for a specific index. See #resolvedVariants JSDoc.
          this.#renderDeckeGroup();
          this.#render();
          // A size change can flip whether this instance pairs with a Decke and
          // move #resolvedVariants()[0] — refresh the gallery (guard no-ops it
          // if the resolved gallery variant id didn't actually change).
          this.#updateGallery();
        },
      });
      row.append(pillLabel);
    }

    if (index > 0) {
      const removeLabel = document.createElement('label');
      removeLabel.className = 'variant-option__button-label';

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'button-unstyled';
      removeButton.textContent = this.dataset.removeKissenLabel ?? 'Kissen entfernen';
      removeButton.addEventListener('click', () => this.#handleRemoveKissen(index));

      removeLabel.append(removeButton);
      row.append(removeLabel);
    }

    wrapper.append(row);

    return wrapper;
  }

  #render() {
    this.#updateFarbeName();
    this.#updatePrice();
    this.#updateLieferzeit();
    this.#updateAddToCartButton();
    this.#preloadAddToCartImage();
    this.#dispatchStateEvent();
    this.#updateUrl();
  }

  /**
   * Preloads the currently-resolved selection's own image so it's already
   * cached by the time #animateFlyToCart() needs it - without this, the
   * fly-to-cart element briefly shows its background-color fallback (black,
   * see snippets/fly-to-cart-styles.liquid) while a not-yet-fetched image
   * decodes, which is most noticeable right after switching Farbe/Kissen/
   * Decke and immediately clicking Add to cart. Mirrors
   * assets/product-form.js's AddToCartComponent#preloadImage, but re-run on
   * every render (not just once on pointerenter) since the resolved image
   * here changes with the selection instead of staying fixed.
   */
  #preloadAddToCartImage() {
    const image = this.#resolvedVariants()[0]?.imageUrl;
    if (image) preloadImage(image);
  }

  /**
   * Disables the main add-to-cart button and swaps its label to the
   * sold-out translation when the currently-resolved selection isn't
   * purchasable — mirrors the same `available` expression
   * #dispatchStateEvent() computes for ConfiguratorStateEvent (which only
   * the sticky add-to-cart bar consumes), applied directly here since this
   * component owns refs.addToCartButton itself.
   */
  #updateAddToCartButton() {
    const resolved = this.#resolvedVariants();
    const available = resolved.length > 0 && resolved.every((v) => v.available);

    this.refs.addToCartButton.disabled = !available;
    // Update the label span specifically, not the button's whole textContent —
    // the button also holds the add-to-cart icon and checkmark-burst markup
    // (see blocks/configurator-variant-picker.liquid) that animateAddToCart()
    // depends on; overwriting textContent would wipe those out on every render.
    this.refs.addToCartButtonLabel.textContent = available
      ? this.dataset.addToCartLabel ?? 'Add to cart'
      : this.dataset.soldOutLabel ?? 'Sold out';
  }

  /**
   * Keeps the Farbe legend's name span (blocks/configurator-variant-picker.liquid)
   * showing the currently selected color, e.g. "Farbe Marine" — server-rendered
   * with the initial Farbe on load, updated here on every #render() so it stays
   * correct after a swatch click.
   */
  #updateFarbeName() {
    this.refs.farbeName.textContent = this.#farbe;
  }

  /**
   * Keeps the `?variant=` URL param in sync with the Decke-pairing Kissen
   * instance's resolved variant (the same one #initializeDefaultSelection
   * reads back on load), so a bookmarked/shared/reloaded link reflects the
   * customer's current selection instead of always landing back on the
   * original variant. Falls back to Kissen 1's own bare variant when no
   * Kissen instance is pairable at all (#deckePairingIndex() returns -1 —
   * e.g. Kissen 1 alone at the 40/40 neck-pillow size) — there's no Decke-
   * pairing instance to represent in that case, but Kissen 1's own bare
   * variant is still a valid, reloadable selection worth reflecting.
   *
   * Deliberately does its own #variants lookup for the relevant Kissen
   * instance, mirroring #resolvedVariants()'s farbe/kissenGroesse/deckeGroesse
   * match for that one instance, rather than indexing into
   * #resolvedVariants()'s return value — that array can be shorter than
   * #kissen (unresolved instances aren't pushed), so its indices don't
   * necessarily line up with #kissen's when #deckePairingIndex() is greater
   * than 0.
   *
   * Uses history.replaceState (no new history entries per change) and only
   * touches the URL when it actually changed, mirroring
   * assets/variant-picker.js's equivalent logic for the native picker.
   */
  #updateUrl() {
    const pairingIndex = this.#deckePairingIndex();
    const urlInstance = pairingIndex === -1 ? this.#kissen[0] : this.#kissen[pairingIndex];
    const deckeGroesse = pairingIndex === -1 ? '' : this.#decke?.deckeGroesse ?? '';
    const urlVariant = urlInstance
      ? this.#variants.find(
          (v) => v.farbe === this.#farbe && v.kissenGroesse === urlInstance.kissenGroesse && v.deckeGroesse === deckeGroesse
        )
      : undefined;
    if (!urlVariant) return;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(urlVariant.id));

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }
  }

  /**
   * Builds the sticky-add-to-cart bar's pill summary: one entry per resolved
   * Kissen instance (Kissen 1 unlabeled-index, Kissen 2+ numbered — same
   * labels #buildKissenRow already uses), plus one for Decke if selected.
   * Order matches #resolvedVariants() (Kissen 1..N, implicit Decke folded
   * into Kissen 1's line) so the sticky bar's pill order matches the
   * configurator's own row order.
   * @returns {{ label: string, value: string }[]}
   */
  #buildPillSummary() {
    /** @type {{ label: string, value: string }[]} */
    const pills = this.#kissen.map((instance, index) => ({
      label: this.#kissenLabel(index),
      value: instance.kissenGroesse,
    }));

    if (this.#decke) {
      pills.push({ label: this.dataset.deckeLabel ?? 'Decke', value: this.#decke.deckeGroesse });
    }

    return pills;
  }

  #dispatchStateEvent() {
    const resolved = this.#resolvedVariants();
    const { priceCents, compareAtCents } = this.#priceTotals(resolved);
    const available = resolved.length > 0 && resolved.every((v) => v.available);
    this.dispatchEvent(
      new ConfiguratorStateEvent({
        farbe: this.#farbe,
        priceCents,
        compareAtCents: compareAtCents > priceCents ? compareAtCents : null,
        available,
        pills: this.#buildPillSummary(),
      })
    );
  }

  /**
   * Refreshes the PDP media gallery for the current configurator state: the
   * bare Kissen-only variant's section render (Kissen photo) when no Decke is
   * selected, otherwise the Farbe's first variant (Freisteller).
   *
   * No-ops when the resolved gallery variant id is unchanged since the last
   * dispatch (`#lastGalleryVariantId`), so it is safe to call from every pill
   * handler that might change `#decke` or the Decke-pairing.
   *
   * The configurator resolves variants entirely client-side (no server
   * round-trip) for its own price/Kissen/Decke UI, so nothing here ever
   * fetches a fresh section render. But the media gallery (see
   * assets/media-gallery.js) only updates itself in response to a real
   * `ProductSelectEvent` carrying freshly-fetched section HTML — the same
   * contract assets/variant-picker.js fulfills for the default variant
   * selector. Without this, picking a color here leaves the gallery showing
   * the previous color's images. This mirrors variant-picker.js's
   * fetchUpdatedSection pattern, scoped to just what media-gallery.js needs.
   *
   * Checks `#preloadedGalleryResponses` first (see that field's doc comment and
   * `#preloadSwatchGallery`) - a hover/focus shortly before this click may already have
   * fetched this exact variant's response. This is a real in-memory cache the code
   * consults itself, not a browser-HTTP-cache assumption: Shopify's product-page response
   * sends `Cache-Control: no-cache, no-store` (confirmed via live-tested response headers),
   * so an ordinary `fetch()` to the same URL is guaranteed to hit the network again - a
   * cache hit here can only come from having stored the response ourselves. Falls through
   * to the normal fetch when nothing was preloaded (e.g. a direct click with no prior
   * hover, or a slow/failed preload that hasn't resolved yet) - identical to today's
   * behavior in that case.
   */
  #updateGallery() {
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return;

    // No Decke selected → the gallery follows the bare Kissen-only variant
    // (#resolvedVariants()[0] — the Decke-pairing or, when nothing pairs, the
    // first Kissen instance), whose featured_media is the Kissen photo. With a
    // Decke selected, keep today's behaviour: the Farbe's first variant, whose
    // section render leads with the Freisteller / styled bedding shot. See
    // docs/superpowers/specs/2026-09-08-configurator-kissen-gallery-image-design.md.
    const resolved = this.#resolvedVariants();
    const variant =
      this.#decke == null && resolved[0]
        ? resolved[0]
        : this.#variants.find((v) => v.farbe === this.#farbe);
    if (!variant) return;

    // No-op when the gallery variant hasn't actually changed — don't even abort
    // an in-flight fetch for the same variant.
    if (variant.id === this.#lastGalleryVariantId) return;
    this.#lastGalleryVariantId = variant.id;
    const dispatchedId = variant.id;

    this.#galleryAbortController.abort();
    this.#galleryAbortController = new AbortController();

    const deferredEventPromise = ProductSelectEvent.createPromise();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions: [{ name: 'Farbe', value: this.#farbe }],
        promise: deferredEventPromise.promise,
      })
    );

    const preloaded = this.#preloadedGalleryResponses.get(variant.id);
    if (preloaded != null) {
      const html = new DOMParser().parseFromString(preloaded, 'text/html');
      deferredEventPromise.resolve({ variant: null, detail: { html } });
      return;
    }

    fetch(`${productUrl}?variant=${variant.id}`, { signal: this.#galleryAbortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        deferredEventPromise.resolve({ variant: null, detail: { html } });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name !== 'AbortError') {
          // The fetch failed, so the gallery still shows the previous variant.
          // Clear the guard so the next interaction that resolves this same id
          // isn't no-op'd into leaving the gallery stale — but only if a newer
          // #updateGallery() call hasn't already taken ownership of the guard.
          if (this.#lastGalleryVariantId === dispatchedId) this.#lastGalleryVariantId = null;
          console.error(error);
        }
      });
  }

  /**
   * Displays the Lieferzeit (delivery time) of the slowest-ranked resolved
   * variant, since every selected variant ships together in one submission.
   * Thin wrapper around #slowestLieferzeit() — see that method for the
   * ranking logic itself, shared with handleAddToCart()'s cart-property
   * value so the display and the submitted property can never disagree
   * (the "Lieferzeit " label is added here, on-screen only — the cart
   * property's own key already says "Lieferzeit", so its value stays bare).
   */
  #updateLieferzeit() {
    const lieferzeit = this.#slowestLieferzeit(this.#resolvedVariants());
    this.refs.lieferzeitDisplay.textContent = lieferzeit ? `Lieferzeit ${lieferzeit}` : '';
  }

  /**
   * Computes the Lieferzeit of the slowest-ranked variant among the given
   * list, per the merchant-configured `lieferzeit_order` setting
   * (fastest-to-slowest, one value per line). A variant whose `lieferzeit`
   * text isn't found in that list is treated as slower than every known
   * value ("slowest of unknown") and logged via console.warn so a
   * data-import mismatch doesn't fail silently. Returns '' for an empty
   * input list.
   * @param {ConfiguratorVariant[]} variants
   * @returns {string}
   */
  #slowestLieferzeit(variants) {
    if (variants.length === 0) return '';

    // Normalized so a variant's lieferzeit metafield text still matches the
    // merchant's own lieferzeit_order setting even when one uses a plain
    // hyphen and the other an en/em dash, or one includes a leading
    // "Lieferzeit " label and the other doesn't — see configurator-shared.js's
    // normalizeLieferzeit.
    const order = (this.dataset.lieferzeitOrder ?? '')
      .split('\n')
      .map((value) => normalizeLieferzeit(value.trim()))
      .filter(Boolean);

    let slowest = variants[0];
    let slowestRank = order.indexOf(normalizeLieferzeit(slowest.lieferzeit));
    this.#warnIfUnknownLieferzeit(slowest, slowestRank, order);

    for (const variant of variants.slice(1)) {
      const rank = order.indexOf(normalizeLieferzeit(variant.lieferzeit));
      this.#warnIfUnknownLieferzeit(variant, rank, order);

      const isUnknown = rank === -1;
      const currentIsUnknown = slowestRank === -1;

      // Unknown windows always outrank known ones ("slowest of unknown").
      // Between two unknowns, or two knowns, higher index (later in the
      // merchant's fastest-to-slowest list) wins.
      let variantIsSlower;
      if (isUnknown !== currentIsUnknown) {
        variantIsSlower = isUnknown;
      } else {
        variantIsSlower = rank > slowestRank;
      }

      if (variantIsSlower) {
        slowest = variant;
        slowestRank = rank;
      }
    }

    // Normalized so a value that includes the merchant's inconsistent
    // leading "Lieferzeit " label doesn't duplicate the "Lieferzeit" text
    // this method's own callers already prefix on-screen and in the cart
    // line property (e.g. "Lieferzeit: Lieferzeit 2-5 Werktage").
    return normalizeLieferzeit(slowest.lieferzeit);
  }

  /**
   * Logs a warning when a variant's lieferzeit text isn't found in the
   * merchant's configured order (rank -1), unless the order is empty
   * (nothing configured yet, so there's nothing meaningful to compare against).
   * @param {ConfiguratorVariant} variant
   * @param {number} rank
   * @param {string[]} order
   */
  #warnIfUnknownLieferzeit(variant, rank, order) {
    if (rank !== -1 || order.length === 0) return;
    console.warn(
      `configurator-variant-picker: lieferzeit value "${variant.lieferzeit}" (variant ${variant.id}) not found in the configured lieferzeit_order setting.`
    );
  }

  /**
   * Sums both the plain price and the "was" (compare-at) price across every
   * currently-resolved variant — shared by #updatePrice() (renders it) and
   * #dispatchStateEvent() (forwards it to the sticky-add-to-cart bar), so
   * this aggregation exists in exactly one place. Each variant contributes
   * its own compareAtPriceCents to the "was" sum only when it's actually a
   * discount (greater than that variant's own price) — a non-sale variant
   * contributes its regular price instead, so it never inflates or
   * deflates whether the BUNDLE as a whole shows as on sale. See
   * docs/superpowers/specs/2026-08-27-configurator-sale-price-design.md.
   * @param {ConfiguratorVariant[]} resolved
   * @returns {{ priceCents: number, compareAtCents: number }}
   */
  #priceTotals(resolved) {
    const priceCents = resolved.reduce((sum, v) => sum + v.priceCents, 0);
    const compareAtCents = resolved.reduce((sum, v) => {
      const contribution = v.compareAtPriceCents && v.compareAtPriceCents > v.priceCents ? v.compareAtPriceCents : v.priceCents;
      return sum + contribution;
    }, 0);
    return { priceCents, compareAtCents };
  }

  #updatePrice() {
    const { priceCents, compareAtCents } = this.#priceTotals(this.#resolvedVariants());
    renderPrice(this.refs.priceDisplay, priceCents, compareAtCents, this.dataset.moneyFormat ?? '', this.dataset.currency ?? 'USD');
  }

  /**
   * Index of the first Kissen instance whose size has any Decke-paired
   * variant for the current Farbe, or -1 if none do. The Decke picker
   * pairs with whichever Kissen instance this returns — normally Kissen 1,
   * but shifts to a later instance when an earlier one has an unpairable
   * size (e.g. the 40/40 neck-pillow size), and shifts back automatically
   * once recomputed after any Kissen change. Recomputed fresh every call —
   * no persistent state, mirroring how #renderDeckeGroup already
   * recomputes #decke from scratch on every relevant render.
   * @returns {number}
   */
  #deckePairingIndex() {
    return this.#kissen.findIndex((instance) =>
      this.#variants.some(
        (v) => v.farbe === this.#farbe && v.kissenGroesse === instance.kissenGroesse && v.deckeGroesse !== ''
      )
    );
  }

  /**
   * Resolves every currently-selected picker to its matching variant.
   * Whichever Kissen instance #deckePairingIndex() returns resolves
   * against the current Decke selection (bare variant if "Keine Decke",
   * combined variant otherwise); every other Kissen instance always
   * resolves against a bare variant, since only one Kissen instance can
   * pair with a Decke at a time.
   * @returns {ConfiguratorVariant[]}
   */
  #resolvedVariants() {
    /** @type {ConfiguratorVariant[]} */
    const resolved = [];
    const pairingIndex = this.#deckePairingIndex();

    this.#kissen.forEach((instance, index) => {
      const deckeGroesse = index === pairingIndex ? this.#decke?.deckeGroesse ?? '' : '';
      const match = this.#variants.find(
        (v) => v.farbe === this.#farbe && v.kissenGroesse === instance.kissenGroesse && v.deckeGroesse === deckeGroesse
      );
      if (match) resolved.push(match);
    });

    return resolved;
  }

  /**
   * Rebuilds the Decke-Größe pill group for the current Farbe + whichever
   * Kissen instance #deckePairingIndex() currently pairs with a Decke.
   * Also resets `#decke` as a side effect whenever the current selection
   * is no longer valid for the derived options (cleared entirely if no
   * Kissen instance has Decke pairings, defaulted to the first option
   * otherwise) — not a pure DOM-output method like #renderKissenList.
   *
   * Always re-renders the Kissen list too (every exit path, including both
   * early returns), since #buildKissenRow's own pill-availability check for
   * the pairing instance depends on the CURRENT `this.#decke` value — this
   * method is the only place that value changes, so any caller that changes
   * it indirectly (by calling this method) would otherwise leave the Kissen
   * pills showing stale availability until some unrelated later render.
   *
   * A caller that already calls #renderKissenList() itself around this call
   * just gets one harmless extra rebuild — cheap, and simpler than auditing
   * every call site for the right order instead.
   *
   * When no Kissen instance is pairable at all (#deckePairingIndex()
   * returns -1 — e.g. every current Kissen is the 40/40 neck-pillow size),
   * renders an info sentence plus a "+ Bettwäsche Set hinzufügen" button
   * instead of leaving this area empty. See
   * docs/superpowers/specs/2026-09-03-bettwaesche-set-for-unpairable-kissen-design.md.
   *
   * #deckeCollapsed (bare-Kissen-arrival state) is only honored when the
   * current selection is ACTUALLY pairable — checked after
   * #deckePairingIndex(), not before. The Kissen collection's own links
   * land on exactly the 40/40 neck-pillow size, which has no Decke
   * pairing at all: without this ordering, "+ Decke hinzufügen" would
   * show first and only reveal "Nackenkissen werden nicht im Set
   * verkauft." one click later — a dead-end affordance. Falling through
   * to the unpairable-Kissen empty state instead shows the correct
   * message immediately. See
   * docs/superpowers/specs/2026-09-03-collapsed-decke-for-kissen-arrival-design.md.
   */
  #renderDeckeGroup() {
    this.refs.deckeGroup.innerHTML = '';

    const pairingIndex = this.#deckePairingIndex();
    const pairingKissenGroesse = this.#kissen[pairingIndex]?.kissenGroesse;
    const options = [
      ...new Set(
        this.#variants
          .filter((v) => v.farbe === this.#farbe && v.kissenGroesse === pairingKissenGroesse && v.deckeGroesse !== '')
          .map((v) => v.deckeGroesse)
      ),
    ];

    if (options.length === 0) {
      this.#decke = null;
      this.#renderNoDeckeAvailable();
      this.#renderKissenList();
      return;
    }

    if (this.#deckeCollapsed) {
      this.#renderDeckeCollapsed();
      this.#renderKissenList();
      return;
    }

    // "Keine Decke" is only a valid persistent choice if the paired Kissen
    // instance actually has an in-stock bare (no-Decke) variant behind it —
    // otherwise #resolvedVariants() would silently fail to resolve that
    // instance at all (its bare-variant lookup would come up empty), which
    // could let handleAddToCart() submit a cart missing that Kissen instance
    // with no error shown. Computed here (before the state-reconciliation
    // chain below) so it can override a stale #deckeExplicitlyNone as well
    // as gate the "Keine Decke" pill itself, further down.
    const bareKissenVariant = this.#variants.find(
      (v) => v.farbe === this.#farbe && v.kissenGroesse === pairingKissenGroesse && v.deckeGroesse === ''
    );
    const canOfferNoDecke = Boolean(bareKissenVariant?.available);

    // Checking `!this.#decke` (not just a stale non-matching value) is required —
    // without it, #decke stays stuck at null forever after passing through a
    // no-Decke-options Kissen size (e.g. 40/40) and back, since
    // `this.#decke && !options.includes(...)` never re-triggers once #decke is
    // already null. That violates the "re-adding Decke defaults to the first
    // available option" rule.
    //
    // Two exceptions that suppress the auto-default below:
    // - #deckeInitiallyNone: a one-shot, cleared here — the very first render
    //   after arriving on a bare Kissen-only `?variant=` link. Honor that
    //   "Keine Decke" arrival instead of auto-selecting options[0]. A later
    //   Farbe / Kissen change still defaults (bare-arrival persistence is
    //   #deckeCollapsed's job, handled above).
    // - #deckeExplicitlyNone: normally PERSISTENT — the shopper picked "Keine
    //   Decke" from the pill group and it stays honored across every later
    //   re-render until they select a Decke (a real size pill, "+ Decke
    //   hinzufügen", or "+ Bettwäsche Set" — each clears the flag). See the
    //   field's doc comment. BUT it's only honored while canOfferNoDecke
    //   stays true — if the paired Kissen instance's bare variant becomes
    //   sold out or stops existing, the flag is force-cleared and the normal
    //   auto-default below runs instead (see the branch below).
    if (this.#deckeInitiallyNone) {
      this.#deckeInitiallyNone = false;
    } else if (this.#deckeExplicitlyNone && canOfferNoDecke) {
      // persistent — leave set
    } else if (this.#deckeExplicitlyNone && !canOfferNoDecke) {
      // The shopper's earlier "Keine Decke" choice no longer has a
      // purchasable bare variant behind it (Farbe/Kissen-size change moved
      // them onto a size where it's sold out or doesn't exist) — it can't
      // be honored anymore, so fall back to the normal auto-default instead
      // of leaving #decke stuck at null with no corresponding pill in the DOM.
      this.#deckeExplicitlyNone = false;
      this.#decke = { deckeGroesse: options[0] };
    } else if (!this.#decke || !options.includes(this.#decke.deckeGroesse)) {
      this.#decke = { deckeGroesse: options[0] };
    }

    // Wraps a label row + the fieldset as siblings, mirroring
    // #buildKissenRow's own structure — NOT a <legend> holding the info
    // link, since <legend> has non-standard flex-item sizing in browsers
    // (a documented quirk: even inside a `display: flex` fieldset, legend
    // retains block-like auto-wrapping behavior no CSS reliably overrides)
    // that previously forced the Kissen row's own inline button onto its
    // own line. A plain <span>, associated to the fieldset via
    // aria-labelledby, sidesteps the quirk while keeping the same
    // accessible fieldset-label semantics.
    const wrapper = document.createElement('div');

    const labelId = `configurator-decke-label-${this.dataset.productId}`;
    const labelRow = document.createElement('div');
    labelRow.className = 'configurator-variant-picker__kissen-legend-row';

    const label = document.createElement('span');
    label.id = labelId;
    label.className = 'configurator-variant-picker__kissen-legend';
    this.#appendSizeLabel(label, this.dataset.deckeLabel ?? 'Decke');
    labelRow.append(label);

    if (this.#hasDeckeInfo) {
      const infoLink = document.createElement('button');
      infoLink.type = 'button';
      infoLink.className = 'configurator-variant-picker__decke-info-link button-unstyled';
      infoLink.textContent = this.dataset.sizeInfoButtonLabel ?? 'Show size information';
      infoLink.addEventListener('click', () => this.handleOpenInfo());
      labelRow.append(infoLink);
    }

    wrapper.append(labelRow);

    if (this.#decke && ConfiguratorVariantPicker.#OVERSIZED_DECKE_GROESSEN.includes(this.#decke.deckeGroesse)) {
      const oversizedNote = document.createElement('p');
      oversizedNote.className = 'configurator-variant-picker__oversized-note';
      oversizedNote.textContent =
        this.dataset.oversizedNoteLabel ?? 'Garnituren in Übergrößen ab 200/200 beinhalten 2 Kopfkissenbezüge.';
      wrapper.append(oversizedNote);
    }

    const fieldset = document.createElement('fieldset');
    fieldset.className = 'variant-option variant-option--buttons';
    fieldset.setAttribute('aria-labelledby', labelId);

    const addOption = (value, text) => {
      // The synthetic "" ("Keine Decke") option is never a real SKU, so it has
      // no variant to look up and is always available.
      const matchingVariant =
        value === ''
          ? null
          : this.#variants.find(
              (v) => v.farbe === this.#farbe && v.kissenGroesse === pairingKissenGroesse && v.deckeGroesse === value
            );
      const isOversized = ConfiguratorVariantPicker.#OVERSIZED_DECKE_GROESSEN.includes(value);
      const optionLabel = buildPillOption({
        value,
        text,
        name: `configurator-decke-${this.dataset.productId}`,
        checked: value === '' ? this.#decke === null : this.#decke?.deckeGroesse === value,
        available: matchingVariant?.available ?? true,
        soldOutLabel: this.dataset.soldOutLabel ?? 'Sold out',
        onChange: () => {
          this.#decke = value === '' ? null : { deckeGroesse: value };
          // "Keine Decke" → set the persistent no-Decke flag so later Farbe /
          // Kissen re-renders don't auto-add a Decke back. A real size →
          // clear it (the shopper now wants a Decke). See #deckeExplicitlyNone's
          // doc comment.
          this.#deckeExplicitlyNone = value === '';
          // Re-render the Decke fieldset itself too, not just #render() —
          // needed so the oversized-set note above it (and anything else
          // here that depends on which Decke is selected, not just which
          // pill is checked) picks up the new selection immediately,
          // rather than staying stale from whenever this method last ran.
          // (#renderDeckeGroup also refreshes the Kissen list itself now —
          // see its own doc comment — since the pairing instance's pill
          // availability depends on the current Decke selection too.)
          this.#renderDeckeGroup();
          this.#render();
          // Crossing the Keine-Decke boundary changes the resolved gallery
          // variant (bare Kissen ↔ Farbe-first); a Decke-size ↔ Decke-size
          // change resolves the same id and the guard no-ops it.
          this.#updateGallery();
        },
        infoIcon:
          isOversized && this.#hasDeckeInfo
            ? {
                markup: this.#infoIconMarkup,
                label: this.dataset.sizeInfoButtonLabel ?? 'Show size information',
                onClick: () => this.handleOpenInfo(),
              }
            : undefined,
      });
      fieldset.append(optionLabel);
    };

    for (const value of options) addOption(value, value);

    // "Keine Decke" offers checkout with just the paired Kissen instance
    // alone — only meaningful if that Kissen size actually has an in-stock
    // bare (no-Decke) variant (canOfferNoDecke, computed above).
    if (canOfferNoDecke) {
      addOption('', this.dataset.noDeckeLabel ?? 'Keine Decke');
    }

    wrapper.append(fieldset);

    this.refs.deckeGroup.append(wrapper);
    this.#renderKissenList();
  }

  /**
   * Renders the empty-state content for `deckeGroup` when no current
   * Kissen instance's size has a Decke pairing (#deckePairingIndex()
   * returned -1): an info sentence, plus a "+ Bettwäsche Set hinzufügen"
   * button that adds a new, guaranteed-pairable Kissen instance. The
   * button itself is omitted (info text only) once data-max-kissen is
   * already reached, mirroring #renderKissenList's own canAddKissen check
   * for the "+ weiteres Kissen hinzufügen" button.
   */
  #renderNoDeckeAvailable() {
    const maxKissen = Number.parseInt(this.dataset.maxKissen ?? '4', 10);
    const canAddKissen = this.#kissen.length < maxKissen;

    const wrapper = document.createElement('div');
    wrapper.className = 'configurator-variant-picker__kissen-legend-row';

    const text = document.createElement('p');
    text.className = 'configurator-variant-picker__no-decke-available';
    text.textContent = this.dataset.noDeckeAvailableLabel ?? 'Nackenkissen werden nicht im Set verkauft.';
    wrapper.append(text);

    if (canAddKissen) {
      const addButton = document.createElement('button');
      addButton.type = 'button';
      addButton.className = 'configurator-variant-picker__add-kissen button-unstyled';
      addButton.textContent = this.dataset.addBettwaescheSetLabel ?? 'Bettwäsche Set hinzufügen';
      addButton.addEventListener('click', () => this.#handleAddBettwaescheSet());
      wrapper.append(addButton);
    }

    this.refs.deckeGroup.append(wrapper);
  }

  /**
   * Renders `deckeGroup`'s collapsed state (#deckeCollapsed): just a
   * "+ Decke hinzufügen" button, no legend — the shopper arrived via a
   * bare Kissen-only link and hasn't asked for a Decke yet. Reuses
   * configurator-variant-picker__add-kissen styling, the same shared
   * "small inline add-action" class already used for "+ weiteres Kissen
   * hinzufügen" and "+ Bettwäsche Set hinzufügen".
   */
  #renderDeckeCollapsed() {
    const wrapper = document.createElement('div');
    wrapper.className = 'configurator-variant-picker__kissen-legend-row';

    const addButton = document.createElement('button');
    addButton.type = 'button';
    addButton.className = 'configurator-variant-picker__add-kissen button-unstyled';
    addButton.textContent = this.dataset.addDeckeLabel ?? '+ Decke hinzufügen';
    addButton.addEventListener('click', () => this.#handleShowDecke());
    wrapper.append(addButton);

    this.refs.deckeGroup.append(wrapper);
  }

  /**
   * Reveals the normal Decke picker in place of the collapsed
   * "+ Decke hinzufügen" button, defaulting to the first available
   * Kissen+Decke combination rather than leaving "Keine Decke" selected.
   *
   * Also clears #deckeInitiallyNone here, not just #deckeCollapsed:
   * #renderDeckeGroup()'s collapsed-state branch returns before ever
   * reaching #deckeInitiallyNone's own one-shot consumption logic, so on
   * a bare-Kissen arrival that flag is still true the first time this
   * method's #renderDeckeGroup() call reaches the auto-default logic —
   * left uncleared, it would incorrectly re-honor the original "Keine
   * Decke" arrival instead of taking the "default to first option"
   * branch, defeating the whole point of this button. Explicitly asking
   * for a Decke supersedes that earlier exception.
   *
   * Also clears #deckeExplicitlyNone: an explicit "+ Decke hinzufügen" click
   * supersedes an earlier "Keine Decke" pill choice the same way it supersedes
   * a bare-Kissen arrival.
   */
  #handleShowDecke() {
    this.#deckeCollapsed = false;
    this.#deckeInitiallyNone = false;
    // "+ Decke hinzufügen" is an explicit request for a Decke — clear the
    // persistent no-Decke flag so the #renderDeckeGroup() below auto-selects one.
    this.#deckeExplicitlyNone = false;
    this.#renderDeckeGroup();
    this.#render();
    // #renderDeckeGroup auto-selects a Decke here (#decke goes non-null) —
    // gallery reverts from the Kissen photo to the Freisteller.
    this.#updateGallery();
  }

  /**
   * Adds a new Kissen instance sized to the first Kissen size (for the
   * current Farbe, across every size — not scoped to any one instance)
   * that has a Decke pairing, guaranteeing #renderDeckeGroup finds it
   * pairable and seeds a Decke selection immediately. If the current
   * Farbe genuinely has no Decke-paired variant for any Kissen size at
   * all, `pairableSize` is undefined and this is a no-op — an expected
   * catalog edge case, not one this method needs to handle further.
   */
  #handleAddBettwaescheSet() {
    const maxKissen = Number.parseInt(this.dataset.maxKissen ?? '4', 10);
    if (this.#kissen.length >= maxKissen) return;

    const pairableSize = this.#availableKissenGroessen().find((size) =>
      this.#variants.some((v) => v.farbe === this.#farbe && v.kissenGroesse === size && v.deckeGroesse !== '')
    );
    if (pairableSize === undefined) return;

    this.#kissen.push({ kissenGroesse: pairableSize });
    // Adding a Bettwäsche Set explicitly wants the seeded Decke — clear the
    // persistent no-Decke flag so #renderDeckeGroup() below selects one.
    this.#deckeExplicitlyNone = false;
    this.#renderKissenList();
    this.#renderDeckeGroup();
    this.#render();
    // Adds a pairable Kissen and #renderDeckeGroup seeds a Decke (#decke goes
    // non-null) — gallery reverts from the Kissen photo to the Freisteller.
    this.#updateGallery();
  }

  /**
   * SVG markup for the info-icon reused by both the Decke-legend button and
   * the oversized-pill "i" icons — read once from a hidden template the
   * Liquid file embeds, rather than duplicating the
   * inline_asset_content output in JS. See
   * blocks/configurator-variant-picker.liquid's infoIconTemplate ref.
   * `<template>.innerHTML` is spec-defined to serialize `.content` (not the
   * template's own light-DOM children), so this correctly returns the SVG
   * markup as a string, safe to assign into another element's innerHTML.
   * @returns {string}
   */
  get #infoIconMarkup() {
    return this.refs.infoIconTemplate?.innerHTML ?? '';
  }

  /**
   * Submits every currently-resolved variant (1-4 Kissen + optionally one
   * Decke folded into Kissen 1's line, per #resolvedVariants) as a single
   * atomic multi-item /cart/add.js POST, tagging every resulting line with
   * one `_set_id` cart-line property so a downstream Cart Transform Function
   * can group them back into one visual set, and one shared `Lieferzeit`
   * property (the same slowest-of-selection value shown on-screen, per
   * #slowestLieferzeit) so every line in the submission carries the delivery
   * time the shopper actually saw before purchasing — matching this store's
   * previous theme's `properties[Lieferzeit]` convention. Omitted entirely
   * (not submitted as an empty string) when there's no known Lieferzeit for
   * the current selection.
   */
  async handleAddToCart() {
    const resolved = this.#resolvedVariants();
    // The button is already disabled for a sold-out selection (see
    // #updateAddToCartButton), but a disabled button can still receive a
    // click in some assistive-tech/programmatic scenarios, so this is
    // checked again here as defense-in-depth rather than relying solely on
    // the disabled attribute to prevent the submission.
    if (resolved.length === 0 || !resolved.every((v) => v.available)) return;

    this.refs.addToCartButton.disabled = true;
    this.refs.errorMessage.hidden = true;

    const lieferzeit = this.#slowestLieferzeit(resolved);

    // /cart/add.js does NOT merge duplicate { id, properties } entries within
    // ONE request's own items array into a single quantity-N line — each
    // array entry always becomes its own cart line, regardless of matching
    // its neighbors exactly. That only happens for a variant+properties pair
    // that already exists in the cart from an EARLIER, separate request.
    // Two Kissen instances of the same size (e.g. Kissen 2 and Kissen 3 both
    // 40/80, same Farbe) share both the same variant.id AND identical
    // properties here (_set_id/Lieferzeit are set once per submission, not
    // per instance) — so without this grouping step they'd submit as two
    // separate quantity-1 lines instead of one quantity-2 line, confirmed via
    // a real Cart Transform Function run showing 3 distinct CartLine ids for
    // what should have been 2 (see
    // docs/superpowers/specs/2026-08-25-cart-transform-design.md).
    /** @type {Map<number, number>} */
    const quantityByVariantId = new Map();
    for (const variant of resolved) {
      quantityByVariantId.set(variant.id, (quantityByVariantId.get(variant.id) ?? 0) + 1);
    }

    // Deterministic, not crypto.randomUUID() — derived from the submission's
    // own (variantId, quantity) pairs, sorted by variant id so submission
    // order never affects the result. Two separate add-to-cart clicks of the
    // IDENTICAL configuration (same variants, same quantities) now produce
    // the same _set_id and land in the same Cart Transform bucket, merging
    // into one bundle line at quantity 2 — matching how native Shopify cart
    // behavior already merges a repeat add of a single plain variant.
    // Lieferzeit is deliberately NOT part of this key: a delivery-estimate
    // change between two back-to-back clicks of the same configuration still
    // merges (keeping the anchor's Lieferzeit, per cart_transform_run.js),
    // same as native quantity-bump doesn't re-check delivery estimates
    // either. A different configuration (different variant ids and/or
    // quantities) always produces a different key, so this can't merge two
    // genuinely different sets together.
    const setId = [...quantityByVariantId]
      .sort(([a], [b]) => a - b)
      .map(([variantId, quantity]) => `${variantId}:${quantity}`)
      .join(',');

    const items = [...quantityByVariantId].map(([variantId, quantity]) => ({
      id: variantId,
      quantity,
      properties: lieferzeit ? { _set_id: setId, Lieferzeit: lieferzeit } : { _set_id: setId },
    }));

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [...quantityByVariantId].map(([variantId, quantity]) => ({
          merchandiseId: String(variantId),
          quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || `Cart add failed with status ${response.status}`);
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'configurator-variant-picker',
          sourceId: this.dataset.productId,
          itemCount: items.length,
          didError: false,
        },
      });
      this.animateAddToCart(resolved[0]?.imageUrl);
    } catch (error) {
      console.error(error);
      deferredEventPromise.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.refs.errorMessage.textContent = this.dataset.addToCartError ?? 'Something went wrong. Please try again.';
      this.refs.errorMessage.hidden = false;
    } finally {
      this.refs.addToCartButton.disabled = false;
    }
  }

  /** @type {number[]} */
  #resetTimeouts = [];

  /**
   * Plays the checkmark-burst success state on the Add to cart button
   * (always) and the fly-to-cart image animation (only when the merchant's
   * add_to_cart_animation setting is on) — the same two effects
   * assets/product-form.js's AddToCartComponent provides for the generic
   * add-to-cart-button.liquid, ported here since the configurator uses its
   * own hand-rolled button/component instead of that one. Called by both
   * #handleAddToCart (above) and configurator-generic-picker.js's
   * addToCart() (via this.#host.animateAddToCart()) on a successful add.
   * @param {string} [imageUrl] - The image to fly to the cart, resolved by
   *   the caller from its own currently-resolved variant(s) (see
   *   ConfiguratorVariant.imageUrl in configurator-shared.js) - NOT read
   *   from a static data attribute, so it reflects whichever
   *   Farbe/Kissen/Decke combination was actually just added, not always
   *   the page-load default. Falls back to data-product-variant-media
   *   (the initially-selected variant's image) when omitted.
   */
  animateAddToCart = async (imageUrl) => {
    const { addToCartButton } = this.refs;

    if (this.dataset.addToCartAnimation === 'true') {
      this.#animateFlyToCart(imageUrl);
    }

    this.#resetTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.#resetTimeouts = [];

    if (addToCartButton.dataset.added !== 'true') {
      addToCartButton.dataset.added = 'true';
    }

    // The onAnimationEnd can trigger a style recalculation so we yield to the main thread first.
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);

    const timeoutId = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');

      const index = this.#resetTimeouts.indexOf(timeoutId);
      if (index > -1) {
        this.#resetTimeouts.splice(index, 1);
      }
    }, 800);

    this.#resetTimeouts.push(timeoutId);
  };

  /**
   * Animates an image flying from the Add to cart button to the header cart
   * icon — mirrors assets/product-form.js's AddToCartComponent#animateFlyToCart
   * exactly (same <fly-to-cart> element, same source/destination wiring).
   * @param {string} [imageUrl] - See animateAddToCart's doc comment. Falls
   *   back to data-product-variant-media (set by
   *   blocks/configurator-variant-picker.liquid from the initially-selected
   *   variant) when omitted.
   */
  #animateFlyToCart(imageUrl) {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = imageUrl || this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {import('@theme/fly-to-cart').FlyToCart} */ (
      document.createElement('fly-to-cart')
    );

    flyToCartElement.classList.add('fly-to-cart--main');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.style.setProperty('--start-opacity', '0');
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Thin forwarding wrapper — the theme's declarative on:click dispatch only
   * ever calls methods on the closest Component-derived custom element
   * instance (this class), never on an arbitrary delegate object, so this
   * method must exist here even though all its logic lives in
   * GenericConfiguratorPicker. Bound via blocks/configurator-variant-picker.liquid's
   * conditional on:click="/handleGenericAddToCart" (generic path only).
   */
  async handleGenericAddToCart() {
    await this.#genericPicker?.addToCart();
  }

  /**
   * Switches to the Größeninformationen view. Bound to the button next to
   * the Decke legend AND the "i" icon on the three oversized Decke pills
   * (both added in #renderDeckeGroup), AND independently to the button next
   * to the Kissen legend (added in #buildKissenRow) — all three call this
   * same method, since there's no per-trigger variation in what opens: the
   * dialog's own content already shows the Kissen and Decke sections side
   * by side whenever each one has content (see blocks/configurator-variant-
   * picker.liquid's kissen_container/decke_container captures).
   *
   * Below the 750px breakpoint this opens a real <dialog> (modal, with a
   * backdrop) instead of the in-card slide panel used at/above it. Both
   * presentations share the exact same .info-content node (title/
   * konfektion/naht image/richtext), relocated via Node.append()/
   * insertBefore() rather than cloned, so there is only ever one live copy
   * of that markup.
   *
   * No-ops when both #hasDeckeInfo and #hasKissenInfo are false — none of
   * this method's triggers get built in that case (see
   * #buildKissenRow/#renderDeckeGroup), but #handleBreakpointChange's own
   * reopen-on-resize call only fires when the dialog/slide was already
   * open, so this guard is purely defensive.
   */
  handleOpenInfo() {
    if (!this.#hasDeckeInfo && !this.#hasKissenInfo) return;

    if (isMobileBreakpoint()) {
      this.refs.infoDialog.append(this.refs.infoContent);
      this.#showInfoDialog();
      return;
    }

    // insertBefore, not append — infoView's info-footer ref is a fixed
    // sibling after info-content in blocks/configurator-variant-picker.liquid's
    // own source order; append() would instead move info-content to the end,
    // placing it after the footer every time this view opens.
    this.refs.infoView.insertBefore(this.refs.infoContent, this.refs.infoFooter);
    this.dataset.view = 'info';
  }

  /**
   * Bound declaratively via the info view's own back button AND the mobile
   * dialog's close-X (both call this same method — see the class doc for
   * handleOpenInfo).
   */
  handleCloseInfo() {
    if (isMobileBreakpoint()) {
      this.#closeInfoDialog();
      return;
    }

    this.dataset.view = 'picker';
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#isBettwaesche) {
      mediaQueryLarge.removeEventListener('change', this.#handleBreakpointChange);
    }
  }

  /** Tracks the scroll position to restore when the dialog closes, mirroring assets/dialog.js's DialogComponent#previousScrollY. */
  #infoDialogPreviousScrollY = 0;

  /**
   * True from the moment #showInfoDialog is called until showModal()
   * actually runs (deferred one requestAnimationFrame, matching
   * assets/dialog.js's DialogComponent#showDialog). Guarding on this flag
   * — set synchronously, unlike infoDialog.open which only flips true once
   * showModal() itself runs — closes a real reentrancy gap: a second call
   * to #showInfoDialog() arriving inside that one-frame window (e.g. two
   * mediaQueryLarge 'change' events firing in quick succession during a
   * resize/rotation, or a fast double-click before the first frame fires)
   * would otherwise also pass an `infoDialog.open` check and call
   * showModal() a second time on the same dialog, which throws
   * InvalidStateError per the <dialog> spec.
   * @type {boolean}
   */
  #infoDialogOpening = false;

  /**
   * Opens the mobile info dialog as a full modal (backdrop, native focus
   * trap, Esc-to-close) — composes the same standalone lockScroll utility
   * assets/dialog.js's DialogComponent uses internally, since
   * ConfiguratorVariantPicker doesn't extend DialogComponent itself (it
   * already extends the plain Component for its non-dialog desktop
   * behavior, and only this one trigger needs dialog mechanics).
   */
  #showInfoDialog() {
    const { infoDialog } = this.refs;
    if (infoDialog.open || this.#infoDialogOpening) return;
    this.#infoDialogOpening = true;

    this.#infoDialogPreviousScrollY = getScrollTop();

    requestAnimationFrame(() => {
      this.#infoDialogOpening = false;
      lockScroll(infoDialog);
      infoDialog.showModal();
      infoDialog.addEventListener('cancel', this.#handleInfoDialogCancel);
      infoDialog.addEventListener('click', this.#handleInfoDialogClick);
    });
  }

  /**
   * Closes the mobile info dialog with the same closing-animation sequence
   * assets/dialog.js's DialogComponent#closeDialog uses (reset animation,
   * force reflow, add dialog-closing, wait for the animation, then close()
   * for real) — dialog-modal's own CSS (assets/base.css) defines the
   * dialog-closing animation this waits on.
   */
  async #closeInfoDialog() {
    const { infoDialog } = this.refs;
    if (!infoDialog.open) return;

    infoDialog.removeEventListener('cancel', this.#handleInfoDialogCancel);
    infoDialog.removeEventListener('click', this.#handleInfoDialogClick);
    infoDialog.style.animation = 'none';
    void infoDialog.offsetWidth;

    infoDialog.classList.add('dialog-closing');
    infoDialog.style.animation = '';

    await onAnimationEnd(infoDialog, undefined, { subtree: false });

    unlockScroll(infoDialog);
    scrollTo({ top: this.#infoDialogPreviousScrollY, behavior: 'instant' });

    infoDialog.close();
    infoDialog.classList.remove('dialog-closing');
  }

  /**
   * <dialog> fires a native 'cancel' event on Escape and closes itself
   * instantly by default — intercepted here (preventDefault) so Escape
   * goes through the same animated #closeInfoDialog sequence as every
   * other close path, mirroring assets/dialog.js's DialogComponent#handleKeyDown.
   * @param {Event} event
   */
  #handleInfoDialogCancel = (event) => {
    event.preventDefault();
    this.refs.infoDialog.removeEventListener('cancel', this.#handleInfoDialogCancel);
    this.#closeInfoDialog();
  };

  /**
   * Mirrors assets/dialog.js's DialogComponent#handleClick: closes the
   * dialog when a click lands outside its content box, since
   * ConfiguratorVariantPicker doesn't extend DialogComponent.
   * @param {MouseEvent} event
   */
  #handleInfoDialogClick = (event) => {
    if (isClickedOutside(event, this.refs.infoDialog)) {
      this.#closeInfoDialog();
    }
  };

  /**
   * If the info panel is open when the viewport crosses the 750px
   * breakpoint (e.g. rotating a tablet, resizing a window), close whichever
   * presentation was open and reopen the other — mirrors
   * assets/theme-drawer.js's ThemeDrawer#onModalBreakpointChange handling
   * of the identical scenario for its own modal-vs-inline drawer.
   *
   * #closeInfoDialog is async (it awaits the closing animation before
   * actually calling dialog.close()) — awaiting it here before reopening
   * matters because handleOpenInfo relocates .info-content via Node.append,
   * and doing that while the dialog is still mid-close-animation would
   * empty the dialog's visible content out from under its own still-playing
   * animation, and could call showModal() again on a dialog that hasn't
   * finished close()-ing yet.
   */
  #handleBreakpointChange = async () => {
    const wasOpenAsDialog = this.refs.infoDialog.open;
    const wasOpenAsSlide = this.dataset.view === 'info';
    if (!wasOpenAsDialog && !wasOpenAsSlide) return;

    if (wasOpenAsDialog) await this.#closeInfoDialog();
    if (wasOpenAsSlide) this.dataset.view = 'picker';

    this.handleOpenInfo();
  };
}

if (!customElements.get('configurator-variant-picker')) {
  customElements.define('configurator-variant-picker', ConfiguratorVariantPicker);
}
